package diana.soleil.design.iraniancanadienbazzar.feature.gems

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.draw.clip
import androidx.compose.ui.platform.LocalLayoutDirection
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.LayoutDirection
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.GemArt
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.gemArtImage
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme
import diana.soleil.design.iraniancanadienbazzar.core.model.Language
import diana.soleil.design.iraniancanadienbazzar.core.model.PoemForm
import diana.soleil.design.iraniancanadienbazzar.core.model.formatCount
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.Res
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_poem_form_ghazal
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_poem_form_masnavi
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_poem_form_other
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_poem_form_qasideh
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_poem_form_qete
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_poem_form_robaei
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_story_read_time
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource

/*
 * Reading-surface pieces shared by the poetry and short-story screens. They exist so the two
 * readers stay visually one family (and so the tale reader can adopt them later): the Persian
 * verse panel, the poem-form label, the dotted byline, and the reading-time estimate.
 */

/** Localized name of a poem's form — the small caption next to the poet on a poem card. */
@Composable
internal fun PoemForm.label(): String = stringResource(
    when (this) {
        PoemForm.Ghazal -> Res.string.gems_poem_form_ghazal
        PoemForm.Robaei -> Res.string.gems_poem_form_robaei
        PoemForm.Masnavi -> Res.string.gems_poem_form_masnavi
        PoemForm.Qasideh -> Res.string.gems_poem_form_qasideh
        PoemForm.Qete -> Res.string.gems_poem_form_qete
        PoemForm.Other -> Res.string.gems_poem_form_other
    },
)

/**
 * "Hafez · 14th century, Shiraz · Ghazal" — one quiet line under a title. Blank parts are dropped,
 * so a poet with no era still reads cleanly. The dot is a plain middle dot, which sits correctly
 * in both Latin and Persian runs.
 */
@Composable
internal fun GemByline(parts: List<String>, modifier: Modifier = Modifier) {
    val visible = parts.filter { it.isNotBlank() }
    if (visible.isEmpty()) return
    Text(
        text = visible.joinToString(" · "),
        style = AppTheme.typography.labelMd,
        color = AppTheme.colors.onSurfaceVariant,
        modifier = modifier,
    )
}

/**
 * The Persian verse, set the way a Persian reader expects it: right-to-left, each line centered so
 * the two hemistichs of a beyt hang from the same axis, in the Farsi type scale so joined
 * letterforms are correct even inside an English or French session. Sits on a soft panel with the
 * section's feather art faded into the corner — the same "watermark over a panel" treatment the
 * joke and fact cards use, so the whole Hidden Gems family reads as one system.
 */
@Composable
internal fun PersianVersePanel(lines: List<String>, modifier: Modifier = Modifier) {
    Box(
        modifier = modifier
            .fillMaxWidth()
            .clip(AppTheme.shapes.card)
            .background(AppTheme.colors.surfaceContainer),
    ) {
        // matchParentSize keeps the watermark out of the panel's measurement: without it the
        // 148dp art set a minimum height, and a two-line poem sat in a panel of mostly air.
        Box(modifier = Modifier.matchParentSize()) {
            Image(
                painter = painterResource(gemArtImage(GemArt.Poetry)),
                contentDescription = null,
                modifier = Modifier
                    .align(Alignment.TopEnd)
                    .size(AppTheme.sizes.gemWatermark)
                    .alpha(WATERMARK_ALPHA),
            )
        }
        CompositionLocalProvider(LocalLayoutDirection provides LayoutDirection.Rtl) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = AppTheme.spacing.lg, vertical = AppTheme.spacing.xl),
                verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                lines.forEach { line ->
                    Text(
                        text = line,
                        style = AppTheme.farsiTypography.bodyLg,
                        color = AppTheme.colors.onSurface,
                        textAlign = TextAlign.Center,
                        modifier = Modifier.fillMaxWidth(),
                    )
                }
            }
        }
    }
}

/**
 * The two-line Persian teaser on a poem card — the opening beyt is how an Iranian recognises a
 * poem, far more than any title we give it. Right-to-left in the Farsi scale, end-aligned so it
 * sits naturally under a Latin title without pretending to be one paragraph with it.
 */
@Composable
internal fun PersianOpeningLines(lines: List<String>, modifier: Modifier = Modifier) {
    if (lines.isEmpty()) return
    CompositionLocalProvider(LocalLayoutDirection provides LayoutDirection.Rtl) {
        Column(modifier = modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.xxs)) {
            lines.forEach { line ->
                Text(
                    text = line,
                    style = AppTheme.farsiTypography.bodyMd,
                    color = AppTheme.colors.onSurfaceVariant,
                    modifier = Modifier.fillMaxWidth(),
                )
            }
        }
    }
}

/**
 * "3 min read" for a body of prose, in the session's digits. Word count over the usual 200 wpm,
 * floored at one minute — a two-paragraph folk tale should not claim "0 min".
 */
@Composable
internal fun readTimeLabel(body: String, lang: Language): String {
    val words = body.split(WHITESPACE).count { it.isNotBlank() }
    val minutes = maxOf(1, (words + WORDS_PER_MINUTE / 2) / WORDS_PER_MINUTE)
    return stringResource(Res.string.gems_story_read_time, lang.formatCount(minutes))
}

/** Reading-line row: a chip-like label on the start side and quiet meta text on the end side. */
@Composable
internal fun ReadingMetaRow(start: @Composable () -> Unit, end: String?, modifier: Modifier = Modifier) {
    Row(
        modifier = modifier.fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
    ) {
        start()
        if (!end.isNullOrBlank()) {
            Text(
                text = end,
                style = AppTheme.typography.labelSm,
                color = AppTheme.colors.onSurfaceVariant,
            )
        }
    }
}

private val WHITESPACE = Regex("\\s+")
private const val WORDS_PER_MINUTE = 200
/** Faint enough to be texture, not illustration — matches the joke/fact card watermark. */
private const val WATERMARK_ALPHA = 0.12f
